import React, {useMemo, useRef, useState} from "react";
import CFAdminNotification from "../../components/CFAdminNotification";
import CFAdminConfirmDialog from "../../components/CFAdminConfirmDialog";
import VinFragmentService from "../../services/VinFragmentService";
import VinImportService from "../../services/VinImportService";
import VehicleConfigService from "../../services/VehicleConfigService";
import {AttributeIdEnum} from "../../constants/AttributeIdEnum";
import SearchableSelect from "./SearchableSelect";
import "./CFAdminVinFragment.scss";

type VinAttribute = {
    attributeCode: number;
    attributeId: number;
    attributeValue: string;
};

type AttributeValueOption = {
    attributeValueId: number;
    attributeValueDescription: string;
};

type VinFragmentDetail = {
    id: number;
    vinSigniPattrnMask: string;
    attributes: Record<string, VinAttribute>;
};

// Drives step 2 behaviour: edit an existing record, clone one (Year only),
// or build a brand-new record from a blank template.
type FormMode = "update" | "clone" | "create";

// Attribute key that stays editable while cloning.
const YEAR_ATTRIBUTE = "Year";

// Attributes that must have a value before an update or create can be saved.
const REQUIRED_ATTRIBUTES = [
    "Make", "Model", YEAR_ATTRIBUTE, "Series", "Drivetrain", "Cylinders",
    "Transmission", "Engine Displacement", "VehicleType", "Fuel",
];

// Acceptable model-year range for create and clone.
const MIN_MODEL_YEAR = 1981;
const MAX_MODEL_YEAR = new Date().getFullYear() + 2;

// Step 1 VIN pattern format: 8 chars, a space, then 2 chars (e.g. 1FDYE1C8 TK).
const VIN_PATTERN_REGEX = /^[A-Z0-9]{8}\s[A-Z0-9]{2}$/;

const ATTRIBUTE_ICONS: Record<string, string> = {
    Make: "\uD83C\uDFF7\uFE0F",
    Model: "\uD83D\uDE99",
    Year: "\uD83D\uDCC5",
    Series: "\uD83C\uDFAF",
    VehicleType: "\uD83D\uDE90",
    BodyStyle: "\uD83D\uDE97",
    DriveTrain: "\u2699\uFE0F",
    Transmission: "\uD83D\uDD27",
    EngineDisplacement: "\uD83D\uDEE2\uFE0F",
    Cylinders: "\uD83D\uDD29",
    Fuel: "\u26FD",
    EngineManufacturer: "\uD83C\uDFED",
    BrakesWeightRatingGVWR: "\u2696\uFE0F",
    BrakesPower: "\uD83D\uDED1",
    RestraintsDriver: "\uD83E\uDEA2",
    Cab: "\uD83D\uDE9A",
    AxleWheelbase: "\uD83D\uDCCF",
    AssemblyCountry: "\uD83C\uDF0E",
    AssemblyPlant: "\uD83C\uDFD7\uFE0F",
};

// Maps the response attribute name to the backend DTO field base name.
// The code column uses the base name, the value column uses `${base}Value`.
const ATTRIBUTE_FIELD_MAP: Record<string, string> = {
    Year: "year",
    Make: "make",
    Model: "model",
    Series: "series",
    Drivetrain: "driveTrain",
    Cylinders: "cylinders",
    Transmission: "transmission",
    "Engine Displacement": "engineDisplacement",
    VehicleType: "vehicleType",
    BodyStyle: "bodyStyle",
    Fuel: "fuel",
    WAxlesWheelbase: "axleWheelbase",
    EngineManufacturer: "engineManufacturer",
    AssemblyPlant: "assemblyPlant",
    AssemblyCountry: "assemblyCountry",
    BrakesPower: "brakesPower",
    BrakesWeightRatingGVWR: "brakesWeightRatingGVWR",
    RestraintsDriver: "restraintsDriver",
    Cab: "cab",
};

// Overrides the label shown in the UI for a given response attribute name.
const ATTRIBUTE_LABELS: Record<string, string> = {
    WAxlesWheelbase: "AxlesWheelbase",
};

type AttributeValidationRule = {
    maxLength?: number;
    numeric?: { precision: number; scale: number };
};

// Validation rules keyed by the response attribute name. These mirror the
// backend column definitions so values are rejected before submission.
const ATTRIBUTE_VALIDATION: Record<string, AttributeValidationRule> = {
    WAxlesWheelbase: {numeric: {precision: 6, scale: 1}},
    EngineManufacturer: {maxLength: 100},
    AssemblyPlant: {maxLength: 150},
    AssemblyCountry: {maxLength: 50},
    BrakesPower: {maxLength: 100},
    RestraintsDriver: {maxLength: 100},
    Cab: {maxLength: 100},
};

// Returns a warning message when the value violates its column rule, else "".
const validateAttributeValue = (name: string, value: string = ""): string => {
    const rule = ATTRIBUTE_VALIDATION[name];
    if (!rule) {
        return "";
    }
    const label = ATTRIBUTE_LABELS[name] ?? name;
    if (rule.maxLength !== undefined && value.length > rule.maxLength) {
        return `${label} must not exceed ${rule.maxLength} characters (currently ${value.length}).`;
    }
    if (rule.numeric && value.trim() !== "") {
        const {precision, scale} = rule.numeric;
        const wholeDigits = precision - scale;
        const numericPattern = new RegExp(String.raw`^\d{1,${wholeDigits}}(\.\d{1,${scale}})?$`);
        if (!numericPattern.test(value.trim())) {
            return `${label} must be numeric with up to ${wholeDigits} digit${wholeDigits === 1 ? "" : "s"} and ${scale} decimal place${scale === 1 ? "" : "s"}.`;
        }
    }
    return "";
};

// Returns a warning when a typed model year is not a 4-digit in-range value.
const validateModelYear = (value: string = ""): string => {
    const trimmed = value.trim();
    if (trimmed === "") {
        return "";
    }
    if (!/^\d{4}$/.test(trimmed)) {
        return "Year must be a 4-digit value.";
    }
    const year = Number(trimmed);
    if (year < MIN_MODEL_YEAR || year > MAX_MODEL_YEAR) {
        return `Year must be between ${MIN_MODEL_YEAR} and ${MAX_MODEL_YEAR}.`;
    }
    return "";
};

// Returns a warning when a VIN pattern does not match the Step 1 format.
const validateVinPattern = (value: string = ""): string => {
    if (value.trim() === "") {
        return "";
    }
    if (!VIN_PATTERN_REGEX.test(value.trim())) {
        return "VIN Pattern must be in the format '1FDYE1C8 TK' (8 characters, a space, then 2 characters).";
    }
    return "";
};

type AttributeFieldProps = {
    name: string;
    attribute: VinAttribute;
    mode: FormMode;
    value: string;
    error: string;
    optionsByAttributeId: Record<number, AttributeValueOption[]>;
    clonePattern: string;
    clonePatternError: string;
    onValueChange: (name: string, value: string) => void;
    onFieldBlur: (name: string) => void;
    onClonePatternChange: (value: string) => void;
    onClonePatternBlur: () => void;
};

// Renders a single step-2 attribute row (plus the clone VIN-pattern field that
// precedes the Year row) so the main component's render stays simple.
function AttributeField({
    name,
    attribute,
    mode,
    value,
    error,
    optionsByAttributeId,
    clonePattern,
    clonePatternError,
    onValueChange,
    onFieldBlur,
    onClonePatternChange,
    onClonePatternBlur,
}: Readonly<AttributeFieldProps>) {
    const isLocked = mode === "clone" && name !== YEAR_ATTRIBUTE;
    const options = attribute.attributeId > 0
        ? optionsByAttributeId[attribute.attributeId] ?? []
        : [];
    const isFreeText = mode === "create"
        ? options.length === 0
        : attribute.attributeCode === 0;
    const currentValue = attribute.attributeValue;
    const currentInOptions = currentValue === ""
        || options.some((option) => option.attributeValueDescription === currentValue);

    return (
        <React.Fragment>
            {mode === "clone" && name === YEAR_ATTRIBUTE && (
                <div className="vin-update-fld">
                    <span className="head">
                        <span className="badge-ico">{"\uD83D\uDD16"}</span>
                        <span className="tag">Vin Pattern</span>
                    </span>
                    <input
                        type="text"
                        className={`vin-update-select vin-update-text${clonePatternError ? " vin-update-invalid" : ""}`}
                        value={clonePattern}
                        maxLength={11}
                        data-testid="clone-vin-pattern"
                        onChange={(event) => onClonePatternChange(event.target.value.toUpperCase())}
                        onBlur={onClonePatternBlur}
                    />
                    {clonePatternError && (
                        <span className="vin-update-warning" role="alert">
                            {clonePatternError}
                        </span>
                    )}
                </div>
            )}
            <div className="vin-update-fld">
                <span className="head">
                    <span className="badge-ico">{ATTRIBUTE_ICONS[name] ?? "\uD83D\uDD16"}</span>
                    <span className="tag">{ATTRIBUTE_LABELS[name] ?? name}</span>
                </span>
                {isFreeText ? (
                    <input
                        type="text"
                        className={`vin-update-select vin-update-text${error ? " vin-update-invalid" : ""}`}
                        value={value}
                        disabled={isLocked}
                        onChange={(event) => onValueChange(name, event.target.value)}
                        onBlur={() => onFieldBlur(name)}
                    />
                ) : (
                    <SearchableSelect
                        value={value}
                        disabled={isLocked}
                        invalid={Boolean(error)}
                        options={[
                            ...(!currentInOptions && currentValue
                                ? [{value: currentValue, label: currentValue}]
                                : []),
                            ...options.map((option) => ({
                                value: option.attributeValueDescription,
                                label: option.attributeValueDescription,
                            })),
                        ]}
                        onChange={(next) => onValueChange(name, next)}
                        onBlur={() => onFieldBlur(name)}
                    />
                )}
                {error && (
                    <span className="vin-update-warning" role="alert">
                        {error}
                    </span>
                )}
            </div>
        </React.Fragment>
    );
}

function CFAdminVinFragment() {
    const notificationRef = useRef<any>();
    const [step, setStep] = useState<1 | 2>(1);
    const [mode, setMode] = useState<FormMode>("update");
    const [vinFragment, setVinFragment] = useState("");
    const [detail, setDetail] = useState<VinFragmentDetail | null>(null);
    const [values, setValues] = useState<Record<string, string>>({});
    const [optionsByAttributeId, setOptionsByAttributeId] = useState<Record<number, AttributeValueOption[]>>({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    // Clone mode lets the user enter a new VIN pattern for the copied record.
    const [clonePattern, setClonePattern] = useState("");
    const [clonePatternTouched, setClonePatternTouched] = useState(false);

    const attributeEntries = useMemo(
        () => (detail ? Object.entries(detail.attributes) : []),
        [detail]
    );

    const filledCount = attributeEntries.filter(
        ([name]) => (values[name] ?? "").trim() !== ""
    ).length;
    const totalCount = attributeEntries.length;
    const percent = totalCount === 0 ? 0 : Math.round((filledCount / totalCount) * 100);

    const hasChanges = useMemo(
        () =>
            attributeEntries.some(
                ([name, attribute]) => (values[name] ?? "").trim() !== (attribute.attributeValue ?? "").trim()
            ),
        [attributeEntries, values]
    );

    const validationErrors = useMemo(() => {
        const errors: Record<string, string> = {};
        attributeEntries.forEach(([name]) => {
            const message = validateAttributeValue(name, values[name] ?? "");
            if (message) {
                errors[name] = message;
            }
        });
        // Year must be a valid 4-digit, in-range value when creating or cloning.
        if (mode === "create" || mode === "clone") {
            const yearMessage = validateModelYear(values[YEAR_ATTRIBUTE] ?? "");
            if (yearMessage) {
                errors[YEAR_ATTRIBUTE] = yearMessage;
            }
        }
        return errors;
    }, [attributeEntries, values, mode]);

    const hasValidationErrors = Object.keys(validationErrors).length > 0;

    // Picks the copy that matches the active form mode without nested ternaries.
    const labelForMode = (labels: Record<FormMode, string>): string => labels[mode];

    // Format error blocks clone submit; the required hint shows only after blur.
    const clonePatternFormatError = mode === "clone" ? validateVinPattern(clonePattern) : "";
    // The new clone pattern must differ from the existing Step 1 pattern.
    const clonePatternSameError = mode === "clone"
    && clonePatternFormatError === ""
    && clonePattern.trim() !== ""
    && clonePattern.trim().toUpperCase() === (detail?.vinSigniPattrnMask ?? "").trim().toUpperCase()
        ? "The given new VIN pattern should not be the same as the previous pattern."
        : "";
    const clonePatternError = clonePatternFormatError
        || clonePatternSameError
        || (mode === "clone" && clonePatternTouched && clonePattern.trim() === "" ? "VIN Pattern is required." : "");

    // A field is required in create/update (full set); clone only enforces Year.
    const isRequiredField = (name: string): boolean => {
        if (mode === "clone") {
            return name === YEAR_ATTRIBUTE;
        }
        return REQUIRED_ATTRIBUTES.includes(name);
    };

    // Error to show under a field: format/range always, required only once blurred.
    const fieldError = (name: string): string => {
        if (validationErrors[name]) {
            return validationErrors[name];
        }
        if (touched[name] && isRequiredField(name) && (values[name] ?? "").trim() === "") {
            return `${ATTRIBUTE_LABELS[name] ?? name} is required.`;
        }
        return "";
    };

    // Enables the submit button per mode: update needs an edit, clone needs a
    // new Year, create needs every attribute filled in.
    const canSubmit = useMemo(() => {
        if (!detail || hasValidationErrors || isSubmitting) {
            return false;
        }
        const allRequiredFilled = REQUIRED_ATTRIBUTES.every(
            (name) => (values[name] ?? "").trim() !== ""
        );
        if (mode === "create") {
            return allRequiredFilled;
        }
        if (mode === "clone") {
            const nextYear = (values[YEAR_ATTRIBUTE] ?? "").trim();
            const patternValid = clonePattern.trim() !== "" && clonePatternFormatError === "" && clonePatternSameError === "";
            return nextYear !== "" && patternValid;
        }
        // update: require an edit and every mandatory attribute filled.
        return hasChanges && allRequiredFilled;
    }, [detail, mode, hasValidationErrors, isSubmitting, hasChanges, values, clonePattern, clonePatternFormatError, clonePatternSameError]);

    const showError = (message: string) => {
        notificationRef.current?.failureMessage(message);
    };

    const handleVinChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setVinFragment(event.target.value.toUpperCase());
    };

    const handleNext = async () => {
        const fragment = vinFragment.trim();
        if (!VIN_PATTERN_REGEX.test(fragment)) {
            showError("Please enter a VIN pattern in the format '1FDYE1C8 TK' (8 characters, a space, then 2 characters).");
            return;
        }
        setIsSubmitting(true);
        try {
            const response = await VinFragmentService.retrieveVinFragment(fragment);
            const data: VinFragmentDetail = response.data;
            // Clone requires an existing record to copy from.
            if (mode === "clone" && !vinFragmentExists(data)) {
                showError("This VIN pattern does not exist, so it cannot be cloned. Please try again with an existing, valid VIN fragment.");
                return;
            }
            const initialValues: Record<string, string> = {};
            Object.entries(data.attributes).forEach(([name, attribute]) => {
                initialValues[name] = attribute.attributeValue;
            });
            await loadAttributeOptions(data);
            setDetail(data);
            setValues(initialValues);
            // Clone starts with a blank VIN pattern the user must supply.
            setClonePattern("");
            setClonePatternTouched(false);
            setStep(2);
        } catch (error) {
            console.error(error);
            showError("Unable to retrieve the VIN fragment. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Update and Clone share the same retrieval; only the mode differs.
    // Clone copies every value but locks all fields except Year on step 2.
    const handleUpdate = async () => {
        setMode("update");
        await handleNext();
    };

    const handleClone = async () => {
        setMode("clone");
        await handleNext();
    };

    // True when retrieveVinFragment returns an existing record for the pattern.
    const vinFragmentExists = (data: any): boolean => {
        if (Array.isArray(data)) {
            return data.length > 0;
        }
        if (!data) {
            return false;
        }
        if ((data.id ?? 0) > 0) {
            return true;
        }
        const attributes = data.attributes ?? {};
        return Object.values(attributes).some(
            (attribute: any) => ((attribute?.attributeValue ?? "") as string).trim() !== ""
        );
    };

    // Create opens a blank form using the same attribute dropdowns as Update.
    // Attribute ids come from the master attribute list; options are loaded per
    // id so the user selects values. The typed VIN pattern becomes the mask.
    const handleCreate = async () => {
        const fragment = vinFragment.trim();
        if (!VIN_PATTERN_REGEX.test(fragment)) {
            showError("Please enter a VIN pattern in the format '1FDYE1C8 TK' (8 characters, a space, then 2 characters).");
            return;
        }
        setIsSubmitting(true);
        try {
            // Block create when the VIN pattern already has an existing record.
            const existing = await VinFragmentService.retrieveVinFragment(fragment);
            if (vinFragmentExists(existing.data)) {
                showError("This VIN pattern already exists and cannot be created. Please try again with a new VIN fragment.");
                return;
            }
            const response = await VehicleConfigService.getVinMappedAttributes();
            const attributes: any[] = response.data?.attributes ?? [];
            // Match on a normalized key so casing/spacing differences between the
            // attribute list and the fragment field names still resolve an id.
            const normalize = (text: string) => (text ?? "").replaceAll(/[^a-z0-9]/gi, "").toLowerCase();
            const idByName: Record<string, number> = {};
            attributes.forEach((attribute) => {
                if (attribute.attributeName) {
                    idByName[normalize(attribute.attributeName)] = attribute.attributeId;
                }
                if (attribute.attributeDescription) {
                    idByName[normalize(attribute.attributeDescription)] = attribute.attributeId;
                }
            });
            // Make/Model/Year may be excluded from the list; seed their known ids.
            idByName[normalize("Make")] ??= Number(AttributeIdEnum.Make);
            idByName[normalize("Model")] ??= Number(AttributeIdEnum.Model);
            idByName[normalize("Year")] ??= Number(AttributeIdEnum.Year);

            const blankAttributes: Record<string, VinAttribute> = {};
            const blankValues: Record<string, string> = {};
            Object.keys(ATTRIBUTE_FIELD_MAP).forEach((name) => {
                const attributeId = idByName[normalize(name)] ?? idByName[normalize(ATTRIBUTE_LABELS[name] ?? "")] ?? 0;
                blankAttributes[name] = {attributeCode: 0, attributeId, attributeValue: ""};
                blankValues[name] = "";
            });

            const template: VinFragmentDetail = {id: 0, vinSigniPattrnMask: fragment, attributes: blankAttributes};
            await loadAttributeOptions(template);
            setMode("create");
            setDetail(template);
            setValues(blankValues);
            setStep(2);
        } catch (error) {
            console.error(error);
            showError("Unable to load vehicle attributes. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const loadAttributeOptions = async (data: VinFragmentDetail) => {
        const attributeIds = Array.from(
            new Set(
                Object.values(data.attributes)
                    .map((attribute) => attribute.attributeId)
                    .filter((attributeId) => attributeId > 0)
            )
        );
        if (attributeIds.length === 0) {
            setOptionsByAttributeId({});
            return;
        }
        const response = await VinImportService.getVehicleAttributeValues({attributeIds});
        const attributeValues: any[] = response.data?.attributeValues ?? [];
        const grouped: Record<number, AttributeValueOption[]> = {};
        attributeValues.forEach((value) => {
            grouped[value.attributeId] ??= [];
            grouped[value.attributeId].push({
                attributeValueId: value.attributeValueId,
                attributeValueDescription: value.attributeValueDescription,
            });
        });
        Object.values(grouped).forEach((options) => {
            options.sort((first, second) =>
                first.attributeValueDescription.localeCompare(second.attributeValueDescription)
            );
        });
        setOptionsByAttributeId(grouped);
    };

    const handleValueChange = (name: string, value: string) => {
        setValues((previous) => ({...previous, [name]: value}));
    };

    const handleFieldBlur = (name: string) => {
        setTouched((previous) => ({...previous, [name]: true}));
    };

    const handleBack = () => {
        setStep(1);
    };

    const showSuccess = (message: string) => {
        notificationRef.current?.successMessage(message);
    };

    const resolveAttributeCode = (attribute: VinAttribute, value: string): number => {
        const options = attribute.attributeId > 0 ? optionsByAttributeId[attribute.attributeId] ?? [] : [];
        const matched = options.find((option) => option.attributeValueDescription === value);
        if (matched) {
            return matched.attributeValueId;
        }
        return value === attribute.attributeValue ? attribute.attributeCode : 0;
    };

    const buildPayload = () => {
        const payload: Record<string, any> = {
            vinSigniPattrnMask: detail?.vinSigniPattrnMask,
        };
        // Clone sends the new pattern separately so the backend keeps the source
        // mask and inserts the copy under the new mask.
        if (mode === "clone") {
            payload.newVinSigniPattrnMask = clonePattern.trim();
        }
        // update mutates the record by id; clone reads that id as the source row
        // to copy; create sends no id so the backend inserts a new row.
        if (mode !== "create") {
            payload.id = detail?.id;
        }
        attributeEntries.forEach(([name, attribute]) => {
            const field = ATTRIBUTE_FIELD_MAP[name];
            if (!field) {
                return;
            }
            const value = values[name] ?? "";
            // In create mode a field is free-text only when no options loaded;
            // otherwise the backend attributeCode marks free-text attributes.
            const isFreeText = mode === "create"
                ? (optionsByAttributeId[attribute.attributeId] ?? []).length === 0
                : attribute.attributeCode === 0;
            if (isFreeText) {
                // Free-text attribute: send only the text value, no id/code.
                payload[field] = null;
                payload[`${field}Value`] = value;
                return;
            }
            payload[field] = resolveAttributeCode(attribute, value);
            payload[`${field}Value`] = value;
        });
        return payload;
    };

    const handleSubmit = () => {
        if (!detail || !canSubmit) {
            return;
        }
        if (hasValidationErrors) {
            showError("Please fix the highlighted attribute values before submitting.");
            return;
        }
        setConfirmOpen(true);
    };

    const resetForm = () => {
        setStep(1);
        setMode("update");
        setVinFragment("");
        setDetail(null);
        setValues({});
        setOptionsByAttributeId({});
        setTouched({});
        setClonePattern("");
        setClonePatternTouched(false);
    };

    const handleConfirmSubmit = async () => {
        setConfirmOpen(false);
        if (!detail) {
            return;
        }
        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            if (mode === "clone") {
                await VinFragmentService.cloneVinFragment(payload);
            } else if (mode === "create") {
                await VinFragmentService.createVinFragment(payload);
            } else {
                await VinFragmentService.newVinFragmentUpdate(payload);
            }
            const successMessage = labelForMode({
                update: "VIN fragment updated successfully.",
                clone: "VIN fragment cloned successfully.",
                create: "VIN fragment created successfully.",
            });
            resetForm();
            showSuccess(successMessage);
        } catch (error) {
            console.error(error);
            const fallbackMessage = labelForMode({
                update: "Unable to update the VIN pattern. Please try again.",
                clone: "Unable to clone the VIN fragment. Please try again.",
                create: "Unable to create the VIN fragment. Please try again.",
            });
            // Prefer the backend-provided message so business rules reach the user.
            const backendMessage = (error as any)?.response?.data?.errorMessage;
            showError(typeof backendMessage === "string" && backendMessage.trim() !== "" ? backendMessage : fallbackMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="vin-update-component">
            <h2 className="fomo-type--title">VIN Fragment Management</h2>

            <div className="vin-update-stepper">
                <div className={`vin-update-step ${step === 1 ? "active" : "done"}`}>
                    <span className="dot">{step === 1 ? "1" : "\u2713"}</span>
                    <span className="label">Enter Details</span>
                </div>
                <span className={`vin-update-bar ${step === 2 ? "filled" : ""}`}/>
                <div className={`vin-update-step ${step === 2 ? "active" : ""}`}>
                    <span className="dot">2</span>
                    <span className="label">Attributes</span>
                </div>
            </div>

            {step === 1 && (
                <div className="vin-update-card">
                    <h3>Step 1 : Enter VIN Fragment</h3>
                    <p className="sub">Provide the VIN fragment to continue.</p>
                    <div className="vin-update-field">
                        <input
                            id="vinFragment"
                            type="text"
                            placeholder=" "
                            maxLength={11}
                            value={vinFragment}
                            data-testid="vin-fragment"
                            onChange={handleVinChange}
                        />
                        <label htmlFor="vinFragment">VIN Pattern (e.g. 1FDYE1C8 TK)</label>
                    </div>
                    <p className="hint">Enter a VIN pattern like '1FDYE1C8 TK' &ndash; 8 characters, a space, then 2
                        characters. Choose <b>Update VIN Fragment</b> to update, <b>Clone VIN Fragment</b> to copy it
                        (Year only), or{" "}
                        <b>Create VIN Fragment</b> to start a blank record.</p>
                    <div className="modal-footer button-frame">
                        <button
                            data-testid="step-reset"
                            className="fvp-button--outline fvp-button"
                            type="button"
                            disabled={isSubmitting}
                            onClick={resetForm}
                        >
                            Reset
                        </button>
                        <div className="right-align">
                            <button
                                data-testid="step-clone"
                                className="fvp-button--outline fvp-button"
                                type="button"
                                disabled={isSubmitting}
                                onClick={handleClone}
                            >
                                Clone VIN Fragment
                            </button>
                            <button
                                data-testid="step-create"
                                className="fvp-button--outline fvp-button"
                                type="button"
                                disabled={isSubmitting}
                                onClick={handleCreate}
                            >
                                Create VIN Fragment
                            </button>
                            <button
                                data-testid="step-next"
                                className="fvp-button button-right-align"
                                type="button"
                                disabled={isSubmitting}
                                onClick={handleUpdate}
                            >
                                Update VIN Fragment
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && detail && (
                <div className="vin-update-card">
                    <h3>
                        Step 2 : {labelForMode({
                        update: "Update VIN Fragment",
                        clone: "Clone Vehicle Attributes",
                        create: "New Vehicle Attributes",
                    })}
                    </h3>
                    <p className="sub">
                        Pattern <b>{detail.vinSigniPattrnMask}</b>{mode === "update" ? ` (ID: ${detail.id})` : ""}
                    </p>
                    {mode === "clone" && (
                        <p className="hint">Cloning an existing record &ndash; provide a new <b>Vin
                            Pattern</b>; <b>Year</b>{" "}
                            and the other values keep their existing entries. Saving creates a new VIN fragment.</p>
                    )}
                    {mode === "create" && (
                        <p className="hint">Creating a new record &ndash; select the values you need, then save.</p>
                    )}

                    <div className="vin-update-grid">
                        {attributeEntries.map(([name, attribute]) => (
                            <AttributeField
                                key={name}
                                name={name}
                                attribute={attribute}
                                mode={mode}
                                value={values[name] ?? ""}
                                error={fieldError(name)}
                                optionsByAttributeId={optionsByAttributeId}
                                clonePattern={clonePattern}
                                clonePatternError={clonePatternError}
                                onValueChange={handleValueChange}
                                onFieldBlur={handleFieldBlur}
                                onClonePatternChange={setClonePattern}
                                onClonePatternBlur={() => setClonePatternTouched(true)}
                            />
                        ))}
                    </div>

                    <div className="vin-update-progress">
                        <div className="info">
                            <b>{filledCount} / {totalCount}</b>
                            <span>attributes completed</span>
                        </div>
                        <div className="track">
                            <div className="fill" style={{width: `${percent}%`}}/>
                        </div>
                        <div className="pct">{percent}%</div>
                    </div>

                    <div className="modal-footer button-frame">
                        <button
                            data-testid="step-back"
                            className="fvp-button--outline fvp-button"
                            type="button"
                            disabled={isSubmitting}
                            onClick={handleBack}
                        >
                            <i className="fvp-left-chevron"/>Back
                        </button>
                        <button
                            data-testid="step-reset"
                            className="fvp-button--outline fvp-button"
                            type="button"
                            disabled={isSubmitting}
                            onClick={resetForm}
                        >
                            Reset
                        </button>
                        <div className="right-align">
                            <button
                                data-testid="step-submit"
                                className="fvp-button button-right-align"
                                type="button"
                                disabled={!canSubmit}
                                onClick={handleSubmit}
                            >
                                {labelForMode({
                                    update: "Update VIN Fragment",
                                    clone: "Clone Vin Fragment",
                                    create: "Save Vin Fragment",
                                })}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <CFAdminNotification ref={notificationRef}/>

            <CFAdminConfirmDialog
                isOpen={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirmSubmit}
                title={labelForMode({
                    update: "Confirm Update",
                    clone: "Confirm Clone ?",
                    create: "Confirm Create ?",
                })}
                text={labelForMode({
                    update: "Are you sure you want to update the VIN pattern with these attributes?",
                    clone: "Are you sure you want to clone this VIN fragment as a new record?",
                    create: "Are you sure you want to save this as a new VIN fragment?",
                })}
            />
        </div>
    );
}

export default CFAdminVinFragment;
